import React, { useState, useEffect } from 'react';
import {
  Form,
  Row,
  Col,
  Spin,
  Card,
  message,
  Button,
  Typography,
  Input,
} from 'antd';
import {
  DeleteOutlined,
  MonitorOutlined,
  ShoppingCartOutlined,
} from '@ant-design/icons';
import CustomTable from 'components/Table';
import { DATE_FRONTEND_FORMAT } from 'utils/formatValues';
import toCurrency from 'utils/toCurrency';
import Uploader from 'components/Uploader';
import Select from 'components/Select';
import { consultarRegistros, procesarPago } from 'api/recaudacion/pagosExternos';
import { getTiposDeReferenciaDePagos } from 'api/catalogos/tiposDeReferenciasDePago';
import { onError } from 'utils/handlers';
import { getReceptoresDePagos } from 'api/catalogos/receptoresDePagos';
import { getLeyendasBancarias } from 'api/recaudacion/leyendasBancarias';
import usePermissionMessage from 'hooks/usePermissionMessage';
import useCorte from 'hooks/useCorte';
import { toMoment } from 'utils/normalizers';

const ENV_NAME = process.env.REACT_APP_ENV_NAME;
const { Text } = Typography;

export const permissionConfig = {
  mainPermission: 'aplicacion_pagosexternos',
  required: [
    'view_unidadderecaudacion',
    'view_banco',
    'view_leyendabancaria',
  ],
};

// eslint-disable-next-line react/prop-types
const PagosExternos = ({ permission }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [bancos, setBancos] = useState([]);
  const [tipoDeReferencia, setTipoDeReferencia] = useState([]);
  const [leyendasBancarias, setLeyendasBancarias] = useState([]);
  const { corte } = useCorte();
  const debounceRef = React.useRef(null);

  usePermissionMessage(permission);

  useEffect(() => {
    let mounted = true;
    const fetchAll = async () => {
      setLoading(true);
      const _bancos = await getReceptoresDePagos();
      const _leyendas = await getLeyendasBancarias();
      if (mounted) {
        setBancos(_bancos);
        setLeyendasBancarias(_leyendas);
        setLoading(false);
      }
    };
    fetchAll();
    return () => { mounted = false; };
    // eslint-disable-next-line
  }, []);

  const fechaCorte = toMoment(corte?.apertura)?.format();
  const fechaArchivo = toMoment(data[0]?.fechaDePago)?.format();

  const debouncePago = async (values) => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    debounceRef.current = setTimeout(async () => {
      const registros = await procesarPago(values);
      if (registros) {
        message.info('Procesando Pagos', 8);
      }
      setLoading(false);
    }, 800);
  };

  const onFinish = async () => {
    setLoading(true);
    const vals = form.getFieldsValue();
    debouncePago(vals);
    setLoading(false);
  };

  const columns = [
    {
      titleText: 'Referencia',
      dataIndex: 'referencia',
      key: 'referencia',
      width: 200,
      render: (_, record) => {
        if (!record.referencia_existente) {
          return (
            <span style={{ color: 'red' }}>
              {record?.referencia}
            </span>
          );
        }
        return (
          <span style={{ color: 'green' }}>
            {record?.referencia}
          </span>
        );
      },
    },
    {
      titleText: 'Fecha de Pago',
      dataIndex: 'fechaDePago',
      key: 'fechaDePago',
      width: 300,
      render: (val) => (val?.format?.(DATE_FRONTEND_FORMAT) || val),
    },
    {
      titleText: 'Convenio',
      dataIndex: 'convenio',
      key: 'convenio',
      width: 200,
    },
    {
      titleText: 'Importe',
      dataIndex: 'importe',
      key: 'importe',
      width: 200,
      render: (val) => (toCurrency(val)),
    },
  ];

  const wipe = () => {
    form.resetFields();
    setData([]);
  };

  const onFinishConsulta = async () => {
    const values = form.getFieldsValue();
    values.entidad = ENV_NAME === 'nayarit' ? 0 : 1;
    const registros = await consultarRegistros(values);
    if (registros) {
      setData(registros);
    }
  };

  const filter = async () => {
    try {
      setLoading(true);
      const { receptor_de_pagos: receptor_de_pago_emitido } = form.getFieldsValue();
      if (receptor_de_pago_emitido) {
        const referencia = await getTiposDeReferenciaDePagos({ receptor_de_pago_emitido });
        setTipoDeReferencia(referencia);
      } else {
        setTipoDeReferencia([]);
      }
      setLoading(false);
    } catch (error) {
      onError(error);
    }
  };

  const fill = (val) => {
    if (val) {
      const clone = [...leyendasBancarias];
      const match = clone.find((i) => i.id === val);
      if (match) {
        form.setFieldsValue({
          clave_de_leyenda_bancaria: match?.clave || '',
        });
      }
    }
  };

  const msgConfirmacion = () => (
    message.info({
      content: (
        <>
          <Row justify="center">
            <strong style={{ marginRight: 3 }}>
              El archivo a procesar  corresponde a un día diferente del corte aperturado
            </strong>
          </Row>
          <Row justify="center">
            <strong style={{ marginRight: 3 }}>
              Fecha de archivo :
              {' '}
            </strong>
            {fechaArchivo}
          </Row>
          <Row justify="center">
            <strong style={{ marginRight: 3 }}>
              Fecha de corte :
              {' '}
            </strong>
            {fechaCorte}
          </Row>
          <Row justify="center">
            <strong style={{ marginRight: 3 }}>
              Para realizar el procesamiento presione continuar o salir  para cancelar el proceso.
            </strong>
          </Row>
          <Row align="middle" justify="center" style={{ width: '100%' }}>
            <Button
              className="no-color success"
              type="link"
              onClick={() => onFinish()}
            >
              Continuar
            </Button>
            <Button
              onClick={() => {
                message.destroy();
              }}
              type="link"
              className="no-color danger"
            >
              Salir
            </Button>
          </Row>
        </>
      ),
      duration: 0,
    })
  );

  return (
    <Row align="center" justify="center" className="container">
      <Spin tip="Cargando..." spinning={loading}>
        <Card
          bordered={false}
          className="form-container"
          title="Aplicación de Pagos"
        >
          <Form
            layout="vertical"
            form={form}
            scrollToFirstError
          >
            <Row gutter={10}>
              <Col xs={24} sm={24} md={8}>
                <Form.Item
                  name="receptor_de_pagos"
                  label="Banco"
                  hasFeedback
                >
                  <Select
                    dataSource={bancos}
                    onChange={filter}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={24} md={8}>
                <Form.Item
                  name="tipo_de_referencia"
                  label="Tipo de Referencia de Pago"
                  hasFeedback
                >
                  <Select
                    dataSource={tipoDeReferencia}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={24} md={8}>
                <Form.Item
                  name="leyenda_bancaria"
                  label="Leyenda Bancaria"
                  hasFeedback
                >
                  <Select
                    dataSource={leyendasBancarias}
                    onChange={fill}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={24} md={8}>
                <Form.Item
                  name="clave_de_leyenda_bancaria"
                  label="Clave de Leyenda Bancaria"
                  hasFeedback
                >
                  <Input disabled />
                </Form.Item>
              </Col>

              <Uploader
                title="Archivo a aplicar"
                limitMB={50}
                formItemName="archivo"
                allowedExtensions={['xlsx', 'pdf', 'txt', 'TXT']}
                allowPreview
              />
              <Col xs={24} sm={24} md={8}>
                <Button
                  onClick={onFinishConsulta}
                  block
                  style={{ marginTop: 21 }}
                >
                  <MonitorOutlined />
                  <Typography.Text>
                    Consultar archivo
                  </Typography.Text>
                </Button>
              </Col>
            </Row>
          </Form>
          <CustomTable
            cols={columns}
            data={data.slice(0, -1)}
            mobileColIndex={0}
            rowKey="id"
            allowSearch={false}
            customActions={[
              {
                onClick: async () => wipe(),
                icon: <DeleteOutlined />,
                text: 'Limpiar campos',
                disabledBySelection: false,
              },
              {
                onClick: async () => {
                  if (fechaCorte !== fechaArchivo) {
                    msgConfirmacion();
                  } else {
                    onFinish();
                  }
                },
                icon: <ShoppingCartOutlined />,
                text: 'Procesar',
                disabledBySelection: false,
              },
            ]}
          />
          <Col xs={24} sm={24} md={8}>
            <Text>
              Referencias totales :
              {' '}
              {data[data?.length - 1]?.referencias_totales || '0'}
            </Text>
          </Col>
          <Col xs={24} sm={24} md={8}>
            <Text>
              Referencias del sistema :
              {' '}
              {data[data?.length - 1]?.referencias_encontradas || '0'}
            </Text>
          </Col>
          <Col xs={24} sm={24} md={8}>
            <Text>
              Referencias duplicadas :
              {' '}
              {data[data?.length - 1]?.referencias_duplicadas || '0'}
            </Text>
          </Col>
          <Col xs={24} sm={24} md={10}>
            <Text>
              Referencias no validas :
              {' '}
              {data[data?.length - 1]?.referencias_no_encontradas || '0'}
            </Text>
          </Col>

        </Card>
      </Spin>
    </Row>
  );
};
export default PagosExternos;
